import { useCallback, useMemo } from "react";
import { Badge, Box, Text } from "@chakra-ui/react";
import { useHistory } from "react-router-dom";
import { NAVBAR_HEIGHT } from "components/navbar/NavbarAdmin";
import GamelightTable from "components/table";
import { TableContextProvider } from "contexts/TableContext";
import { Column, Row } from "hooks/useTable";
import useSuperAdmin from "hooks/useSuperAdmin";
import {
  getAllPartnerAppExclusions,
  getPartnersWithCampaigns,
} from "views/admin/appTargeting/services";

interface PartnerRow {
  partnerId: number;
  companyName: string;
  contact: string;
  mail: string;
  hiddenRules: number;
}

interface CustomColumn extends Omit<Column<PartnerRow>, "columnKey"> {
  columnKey: string;
}

const AppTargetingPartners = () => {
  document.title = "Gamelight - App Targeting";

  const isSuperAdmin = useSuperAdmin();
  const history = useHistory();

  const fetchData = useCallback(async () => {
    const [partners, exclusions] = await Promise.all([
      getPartnersWithCampaigns(),
      getAllPartnerAppExclusions(),
    ]);

    const rulesByPartner = exclusions.reduce((acc, row) => {
      acc[row.partnerId] = (acc[row.partnerId] ?? 0) + 1;
      return acc;
    }, {} as Record<number, number>);

    const data: PartnerRow[] = partners.map((partner) => ({
      partnerId: partner.partnerId,
      companyName: partner.companyName || "-",
      contact: [partner.firstName, partner.lastName].filter(Boolean).join(" "),
      mail: partner.mail,
      hiddenRules: rulesByPartner[partner.partnerId] ?? 0,
    }));

    // partners with rules first, then by id
    data.sort(
      (a, b) =>
        Number(b.hiddenRules > 0) - Number(a.hiddenRules > 0) ||
        b.partnerId - a.partnerId
    );

    return { data, total: data.length };
  }, []);

  const columns = useMemo(() => {
    const COLUMNS: CustomColumn[] = [
      {
        columnKey: "companyName",
        header: "COMPANY NAME",
        render: (row: PartnerRow) => (
          <Text fontWeight="medium">{row.companyName}</Text>
        ),
      },
      {
        columnKey: "partnerId",
        header: "PARTNER ID",
        render: (row: PartnerRow) => (
          <Badge colorScheme="blue" variant="outline">
            {row.partnerId}
          </Badge>
        ),
      },
      {
        columnKey: "contact",
        header: "CONTACT",
        render: (row: PartnerRow) => (
          <Box>
            <Text>{row.contact || "-"}</Text>
            <Text fontSize="sm" color="gray.500">
              {row.mail}
            </Text>
          </Box>
        ),
      },
      {
        columnKey: "hiddenRules",
        header: "HIDDEN IN",
        render: (row: PartnerRow) =>
          row.hiddenRules > 0 ? (
            <Badge colorScheme="red" variant="solid">
              {row.hiddenRules} rule{row.hiddenRules === 1 ? "" : "s"}
            </Badge>
          ) : (
            <Text color="gray.500">Shown everywhere</Text>
          ),
      },
    ];
    return COLUMNS;
  }, []);

  const ROW_CONFIG: Row<PartnerRow> = {
    onClick(row) {
      history.push(`/app-targeting/${row.partnerId}`);
    },
    rowStyle() {
      return { cursor: "pointer" };
    },
  };

  if (!isSuperAdmin) {
    return <></>;
  }

  return (
    <TableContextProvider>
      <Box flex={1} minH={"90vh"} pt={NAVBAR_HEIGHT} px={4}>
        <GamelightTable
          columns={columns as any}
          fetchData={fetchData}
          uniqueKey="partnerId"
          title="App Targeting"
          shouldSearch
          searchPlaceHolder="Search partners..."
          rowConfig={ROW_CONFIG}
        />
      </Box>
    </TableContextProvider>
  );
};

export default AppTargetingPartners;
